import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import FeedbackForm from "./components/FeedbackForm";

const bioPoints = [
  { label: "职业", value: "演员 / 影视艺人" },
  { label: "国籍", value: "日本" },
  { label: "出道", value: "2019 年" },
  { label: "代表作", value: "《辉煌之路》《银座之夜》《东京往事》" },
];

const sources = [
  "官方公告与经纪公司发布的正式消息",
  "主流影视媒体的采访与报道",
  "影片官方发布的预告、海报与幕后花絮",
  "公开活动与电影节的现场记录",
];

export default function About() {
  usePageMeta(
    "关于濑户环奈 - 濑户环奈官网",
    "了解日本女演员濑户环奈的个人介绍、演艺经历，以及本站的定位、资讯来源与版权声明。",
  );

  return (
    <PageShell>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于濑户环奈" }]} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center mt-6">
              <div>
                <span className="text-xs font-medium tracking-[0.2em] text-primary-500 uppercase">
                  About
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-3 mb-4 leading-tight">
                  濑户环奈
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  日本女演员濑户环奈以清新自然的表演风格与细腻的情感表达为观众所熟知。
                  从 2019 年银幕出道至今，她先后出演多部电影与电视剧，戏路横跨青春、文艺、悬疑与都市题材，
                  并凭借《东京往事》中的表现获得年度新人女演员奖。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {bioPoints.map((point) => (
                    <div
                      key={point.label}
                      className="rounded-lg bg-background-50 border border-background-200 p-4"
                    >
                      <p className="text-xs text-foreground-400 mb-1">{point.label}</p>
                      <p className="text-sm text-foreground-800 font-medium">{point.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=cinematic%20elegant%20portrait%20of%20a%20young%20Japanese%20woman%20with%20long%20dark%20hair%20in%20a%20soft%20cream%20dress%2C%20warm%20studio%20lighting%2C%20sakura%20pink%20and%20neutral%20tones%2C%20editorial%20photography%2C%20high%20detail&width=1200&height=1500&seq=about-01&orientation=portrait"
                  alt="濑户环奈个人写真"
                  title="濑户环奈 - 日本女演员"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-10">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                演艺经历
              </h2>
              <div className="flex flex-col gap-4">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  濑户环奈于 2019 年凭借青春电影《夏日回声》正式登上大银幕，片中那个带着未经雕琢灵气的少女让观众记住了她的名字。
                  此后她持续在电影与电视剧之间探索，从《初雪》里清冷的少女，到《东京往事》中跨越时代的家族继承者，
                  再到《辉煌之路》里倔强的舞者，她始终尝试用不同的角色拓宽自己的表演边界。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  近年来，濑户环奈相继出演《银座之夜》《海边的信》等话题作品，并挑战了《黎明之前》这样硬朗的悬疑题材。
                  她表示，希望未来能尝试更多“离自己很远”的角色，用表演去理解不同的人生。
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                网站定位
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                濑户环奈资讯站是一个专注其影视作品与最新动态的粉丝资讯平台，面向喜爱濑户环奈的观众以及关注日本影视娱乐的读者。
                我们希望把分散在各处的作品资料与新闻资讯整理成结构清晰、便于查阅的一站式页面，
                让你可以快速了解她有哪些作品、近期又发生了什么。
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg bg-primary-50 border border-primary-200 p-4">
                  <i className="ri-book-open-line text-xl text-primary-600"></i>
                  <p className="text-sm font-medium text-foreground-900 mt-3 mb-1">作品资料库</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">系统整理的电影与电视剧作品资料</p>
                </div>
                <div className="rounded-lg bg-accent-50 border border-accent-200 p-4">
                  <i className="ri-newspaper-line text-xl text-accent-600"></i>
                  <p className="text-sm font-medium text-foreground-900 mt-3 mb-1">动态资讯</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">官方公告、活动报道与媒体采访汇总</p>
                </div>
                <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-4">
                  <i className="ri-heart-line text-xl text-secondary-600"></i>
                  <p className="text-sm font-medium text-foreground-900 mt-3 mb-1">本地收藏</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">在浏览器本地标记与收藏喜爱的作品</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                资讯来源与版权声明
              </h2>
              <ul className="flex flex-col gap-3 mb-5">
                {sources.map((source) => (
                  <li key={source} className="flex items-start gap-3">
                    <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs mt-0.5">
                      <i className="ri-check-line"></i>
                    </span>
                    <span className="text-sm text-foreground-700 leading-relaxed">{source}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-foreground-600 leading-relaxed rounded-lg bg-background-100 border border-background-200 p-4">
                本站为粉丝资讯站点，所有影视作品与新闻内容均为示范填充数据，图片为示意图片，
                不构成任何官方信息。姓名、作品与相关标识的相关权利归其合法权利人所有。
                本站与濑户环奈本人及其所属经纪公司无任何关联。如内容涉及版权问题，请通过下方反馈渠道与我们联系。
              </p>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 flex flex-col gap-6">
              <FeedbackForm />
              <div className="rounded-lg bg-primary-50 border border-primary-200 p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                  联系我们
                </h3>
                <div className="flex flex-col gap-3 text-sm text-foreground-700">
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line text-primary-600"></i>
                    contact@seto-kanna-fans.example.com
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-time-line text-primary-600"></i>
                    工作日 10:00 - 18:00（JST）
                  </p>
                </div>
                <p className="text-xs text-foreground-500 mt-4 leading-relaxed">
                  反馈内容仅用于网站改进，本站不会将其用于其他用途。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}